import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@apollo/client/react"
import { NetworkStatus } from "@apollo/client/core"
import debounce from "lodash/debounce"
import analytics from "@love-irish/analytics"
import GLOBAL_SEARCH from "./global_search.graphql"
import { groupHits, type SearchGroup, type SearchHit } from "./search_hit"

// Don't fire until there are at least this many non-space characters — single-letter queries match
// half the site and aren't useful as a typeahead.
export const MIN_QUERY_LENGTH = 2

interface GlobalSearchData {
  search: SearchHit[]
}

export interface UseGlobalSearchResult {
  // The debounced, trimmed query actually sent to the server (may lag the input by one debounce).
  query: string
  // True once the query is long enough to search — drives whether the dropdown should show.
  active: boolean
  loading: boolean
  groups: SearchGroup[]
  hits: SearchHit[]
  total: number
}

// Debounced, self-throttling multi-model search. A lodash-debounced action commits the query, which
// drives the Apollo request, so a burst of keystrokes collapses to one request per debounce window (this
// discipline matters: an un-debounced query-per-keystroke typeahead is what rate-limited us once before).
// The search.executed analytics event fires on completion — Apollo v4 removed useQuery's onCompleted (its
// render-callback side effects were unreliable), so the recommended replacement is to react to the settled
// result in an effect, gated on networkStatus === ready. It goes to all analytics services (PostHog +
// Ahoy) client-side, since search runs through the shared GraphQL endpoint (no per-feature controller).
export function useGlobalSearch(
  input: string,
  {
    limit = 20,
    debounceMs = 250,
    source = "header",
  }: { limit?: number; debounceMs?: number; source?: string } = {}
): UseGlobalSearchResult {
  // The committed query that actually drives the request (updated by the debounced action below).
  const [query, setQuery] = useState("")

  // The debounce only throttles the refetch (commits the query); analytics fires on completion below.
  const commitSearch = useMemo(
    () => debounce((next: string) => setQuery(next), debounceMs),
    [debounceMs]
  )

  // Feed each keystroke into the debounced action; cancel any pending run when it changes / on unmount.
  useEffect(() => {
    commitSearch(input.trim())
  }, [input, commitSearch])
  useEffect(() => () => commitSearch.cancel(), [commitSearch])

  const active = query.length >= MIN_QUERY_LENGTH

  const { data, loading, networkStatus } = useQuery<GlobalSearchData>(GLOBAL_SEARCH, {
    variables: { query, limit },
    skip: !active,
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true, // deliver networkStatus transitions so the completion effect runs
  })

  // onCompleted replacement (Apollo v4): fire once when the request settles. ready = 7 covers a network
  // response and a cache hit; dedupe by query so re-renders for the same result don't re-fire.
  const firedFor = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (networkStatus !== NetworkStatus.ready || !active || !data?.search) return
    if (firedFor.current === query) return
    firedFor.current = query
    analytics.track("search.executed", {
      query,
      result_count: data.search.length,
      source,
    })
  }, [networkStatus, active, data, query, source])

  const hits = useMemo<SearchHit[]>(() => {
    if (!active || !data?.search) return []
    return data.search
  }, [active, data])

  const groups = useMemo(() => groupHits(hits), [hits])

  return {
    query,
    active,
    // Only surface loading while a search is actually in flight (not for sub-min-length input).
    loading: active && loading,
    groups,
    hits,
    total: hits.length,
  }
}
